import { AxiosError } from "axios";
import _ from "lodash";
import moment from "moment";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getContractApply } from "src/api/contract/contract-api";
import { ContractApplicationModel, ContractManageList, searchStatusOption, searchTypeOption } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { ApplicationFormModel } from "src/api/product/product-types";
import { Order, PageMeta } from "src/api/public-types";
import { BaseButton, BaseInput, BaseInputWithSearch, BaseSelect, BaseTable } from "src/components";
import { MultiSelectFilter } from "src/components/multiSelect";
import RangeDatepicker from "src/components/RangeDatepicker";
import SearchResetButton from "src/components/SearchResetButton";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import SearchBuildingPopup from "src/pages/commonPopup/SearchBuildingPopup";
import SearchOrgPopup from "src/pages/commonPopup/SearchOrgPopup";
import useUsageApplication, { ProductApplication } from "src/pages/product/hooks/useUsageApplication";
import { approvalTypeOptions, ProductTypeLabels, ProductTypes } from "src/pages/product/product-types";
import ExcelDownloadButton from "src/pages/user/list/ExcelDownloadButton";
import { useModal } from "src/recoil/modalState/hook";
import { qsParse, ViewYmdFormat, YmdFormat } from "src/utils/common-util";
import { downloadExcelByColumns } from "src/utils/excelDownload";
import { CONTRACT_APPROVAL_STATUS_OPTIONS } from "../contract-constants";
import UsageApplicationDetail from "../form/components/application/component/UsageApplicationDetail";
import UsageApplicationForm from "../form/components/application/component/UsageApplicationForm";
import { useS2Contract } from "../hooks/useS2Contract";
import { columns } from "./columns/Columns";

const ProductType = [
  { value: "", label: "전체" },
  { value: ProductTypes["FULL_COURT"], label: ProductTypeLabels["FULL_COURT"] },
  { value: ProductTypes["TIME_COURT"], label: ProductTypeLabels["TIME_COURT"] },
  { value: ProductTypes["MAINTENANCE_FEE"], label: ProductTypeLabels["MAINTENANCE_FEE"] },
  { value: ProductTypes["NON_RESIDENT_OFFICE"], label: ProductTypeLabels["NON_RESIDENT_OFFICE"] },
  { value: ProductTypes["DIGITAL_PRODUCT"], label: ProductTypeLabels["DIGITAL_PRODUCT"] },
];
const ContractList = () => {
  const { isAuthority } = usePartnerAuthority();
  const { setAbstractModalZ1, setAbstractModalZ2, setBaseModal } = useModal();
  //계약이 s2 인지 체크
  const { isS2Contract } = useS2Contract();

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const navigate = useNavigate();
  const location = useLocation();
  const [isBuildingOpen, setIsBuildingOpen] = useState(false);

  const [isOrgOpen, setIsOrgOpen] = useState(false);

  const contractListColumns = useMemo(() => {
    // s2 계약일 경우 상품유형, 정원/이용자 수 컬럼 제외
    return columns.filter((column) =>
      isS2Contract ? column.accessor !== "spaceProductType" && column.accessor !== "memberMaxNums" && column.accessor !== "memberTotCount" : true,
    );
  }, [isS2Contract]);

  const queryParams: any = useMemo(() => {
    const _queryParams: any = qsParse(location.search);

    // page, size, sort 없이 최초 진입했을때 default 값 바인딩
    if (!_queryParams?.page) {
      _queryParams.page = 0;
    }
    if (!_queryParams?.size) {
      _queryParams.size = 20;
    }

    if (!_queryParams?.sort) {
      _queryParams.sort = {
        orders: [{ property: "contractManageId", direction: "DESC" }],
      };
    }

    if (isS2Contract) {
      _queryParams.productTypes = "TIME_COURT";
    }

    if (_queryParams?.id) {
      delete _queryParams?.id;
    }
    if (!_queryParams?.searchType) {
      _queryParams.searchType = "";
    }
    if (!_queryParams?.searchValue) {
      _queryParams.searchValue = "";
    }

    const dateTimes = _.cloneDeep(dateRange);
    if (_queryParams?.searchStartTime !== undefined && _queryParams?.searchStartTime !== "") {
      _queryParams.searchStartTime = moment(_queryParams?.searchStartTime).toDate();

      dateTimes[0] = moment(_queryParams?.searchStartTime).toDate();
    } else {
      dateTimes[0] = null;
    }
    if (_queryParams?.searchEndTime !== undefined && _queryParams?.searchEndTime !== "") {
      _queryParams.searchEndTime = moment(moment(_queryParams?.searchEndTime).format(ViewYmdFormat.YYYY_MM_DD)).toDate();

      dateTimes[1] = moment(_queryParams?.searchEndTime).toDate();
    } else {
      dateTimes[1] = null;
    }

    setDateRange(dateTimes);

    if (!_queryParams?.searchValue) {
      _queryParams.searchValue = "";
    }

    if (!_queryParams?.approvalType) {
      _queryParams.approvalType = undefined;
    }
    if (!_queryParams?.approvalStatus) {
      _queryParams.approvalStatus = undefined;
    }

    if (_queryParams.buildingName === undefined || _queryParams.buildingName === "") {
      _queryParams.buildingName = "";
      delete _queryParams.buildingId;
    }
    if (_queryParams.orgName === undefined || _queryParams.orgName === "") {
      _queryParams.orgName = "";
      delete _queryParams.mbOrganizationId;
    }

    return _queryParams;
  }, [location]);
  const [params, setParams] = useState<any>({ ...queryParams });
  const [data, setData] = useState<ContractManageList[] | undefined>(undefined);
  const [pagination, setPagination] = useState<PageMeta>();

  //호출부
  const { executeAsync } = useApiOperation(getContractApply);

  // 쿼리파라미터 정보와 함께 네비게이션
  const navigateWithQueryParams = useCallback(
    (passParams?: any) => {
      const newQueryParams = { ...params, ...(passParams || {}) };
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true, encode: true });
      setParams(newQueryParams);
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [params, navigate, location.pathname],
  );

  const settingBuilding = useCallback(
    (bd: any) => {
      setIsBuildingOpen(false);

      navigateWithQueryParams({ ...params, ...{ buildingId: bd.id, buildingName: bd.buildingName } });
    },
    [params],
  );

  const settingOrg = useCallback(
    (org: any) => {
      setIsOrgOpen(false);

      // 개인이면 ceoName 사업자면 name
      org.name = org.classifications.includes("INDIVIDUAL") ? org.ceoName : org.name;

      navigateWithQueryParams({ ...params, ...{ mbOrganizationId: org.id, orgName: org.name } });
    },
    [params],
  );

  const callList = useCallback(
    async (excelCall?: boolean) => {
      const param = _.cloneDeep(queryParams);
      if (param.buildingName === undefined || param.buildingName === "") {
        delete param.buildingId;
      }
      if (param.orgName === undefined || param.orgName === "") {
        delete param.mbOrganizationId;
      }
      if (dateRange[0]) {
        let startTime = moment(dateRange[0]).format(YmdFormat.YYYY_MM_DD) + "T00:00:00.000+09:00";
        param.searchStartTime = startTime;
        setParams({ ...param, ...{ searchStartTime: startTime } });
      }
      if (dateRange[1]) {
        let endTime = moment(dateRange[1]).format(YmdFormat.YYYY_MM_DD) + "T23:59:59.999+09:00";

        param.searchEndTime = endTime;
        setParams({ ...param, ...{ searchEndTime: endTime } });
      }
      param.searchValue = param.searchValue ? decodeURIComponent(String(param.searchValue)) : "";
      setParams({ ...queryParams, ...param });
      let arrData: any = [];

      if (excelCall) {
        const newCall = _.cloneDeep(params);
        newCall.page = 0;
        newCall.size = 1;
        const check: any = await executeAsync(newCall);
        const { meta } = check.data;
        const fixedCallSize = 100;
        if (meta?.pageMeta?.totalElements > 0) {
          let callSize = Math.floor(meta?.pageMeta?.totalElements / fixedCallSize);
          const dvSize = meta?.pageMeta?.totalElements % fixedCallSize;

          if (dvSize > 0) {
            callSize = callSize + 1;
          }

          let callList: any = [];
          for (let i = 0; i < callSize; i++) {
            callList.push(executeAsync({ ...newCall, page: i, size: fixedCallSize }));
          }
          const result2: any = await Promise.all(callList);
          result2.forEach((response: any) => {
            arrData = [...arrData, ...response.data.data.content];
          });
        }
        if (arrData.length === 0) {
          setBaseModal({ isOpen: true, title: "조회된 데이터가 없습니다.", btnRightTitle: "확인", onClick: () => setBaseModal({ isOpen: false }) });
          return;
        }

        return arrData;
      } else {
        let response: any = await executeAsync(param);

        setData(response.data.data.content);
        setPagination(response.data.meta.pageMeta);
      }
    },
    [dateRange, params, executeAsync, queryParams],
  );

  const handleOnDateRangeChange = (dateRange: [Date | null, Date | null]) => {
    setDateRange(dateRange);
    let startTime = "";
    let endTime = "";

    if (dateRange[0] === null && dateRange[1] === null) {
      navigateWithQueryParams({ ...params, searchStartTime: undefined, searchEndTime: undefined, page: 0 });
      return;
    }

    if (dateRange[0]) {
      startTime = moment(dateRange[0]).format(YmdFormat.WITH_TIME_ZONE);
      setParams({ ...params, ...{ searchStartTime: startTime } });
    }
    if (dateRange[1]) {
      endTime = moment(dateRange[1]).format(YmdFormat.WITH_TIME_ZONE);

      navigateWithQueryParams({ ...params, ...{ searchStartTime: startTime, searchEndTime: endTime, page: 0 } });
    }
  };

  const returnContractSteps = useMemo(() => {
    let rtn: any = [];

    if (params.contractStep !== undefined && params.contractStep !== "") {
      rtn = params.contractStep.split(",");
    }

    return rtn;
  }, [params.contractStep]);

  useEffect(() => {
    callList(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const showDownloadResult = (success: boolean, error?: any) => {
    setBaseModal({
      isOpen: true,
      title: success ? "엑셀 다운로드가 완료되었습니다." : "엑셀 다운로드에 실패하였습니다.",
      ...(error && { children: String(error) }),
      btnRightTitle: "확인",
      onClick: () => setBaseModal({ isOpen: false }),
    });
  };

  const handleClickExcelDownloadButton = async () => {
    let result: any;

    try {
      result = await callList(true);
    } catch (error) {
      showDownloadResult(false, error instanceof AxiosError ? undefined : error);
      return;
    }

    const prefixLabel = isS2Contract ? "공간예약 신청" : "신청/계약";
    const fileNamePrefix =
      dateRange[0] && dateRange[1]
        ? `${prefixLabel}-${moment(dateRange[0]).format("YYYYMMDD")}~${moment(dateRange[1]).format("YYYYMMDD")}`
        : `${prefixLabel}-전체`;

    await downloadExcelByColumns<ContractManageList>({
      data: result,
      columns: contractListColumns,
      fileNamePrefix,
      // 이용신청서는 Y/N 으로 엑셀 추출 (excelValue 사용)
      excludeAccessors: [],
      onNoData: () => {
        setBaseModal({ isOpen: true, title: "조회된 데이터가 없습니다.", btnRightTitle: "확인", onClick: () => setBaseModal({ isOpen: false }) });
      },
      onSuccess: () => {
        showDownloadResult(true);
      },
      onError: (error) => {
        showDownloadResult(false, error);
      },
    });
  };

  const { getApplicationSummury, getContractUsageApplication, onUpdateContractApplication } = useUsageApplication();

  const [contractApplication, setContractApplication] = useState<ContractApplicationModel | null>();

  // 이용신청서 모달 오픈
  const getContractApplication = async (passContractId: number) => {
    const result = await getContractUsageApplication({ contractId: Number(passContractId) });
    setContractApplication(result);
  };

  // 계약 신청서 수정
  const onUpdateCtApplication = (formData: ApplicationFormModel, contractId: number) => {
    setBaseModal({
      isOpen: true,
      title: "저장하시겠습니까?",
      onClick: () => {
        execute();
      },
      onClose: () => {
        setBaseModal({ isOpen: false });
      },
      btnRightTitle: "확인",
      btnLeftTitle: "취소",
    });

    const execute = async () => {
      const result = await onUpdateContractApplication({ contractId: Number(contractId), formData: formData });
      if (result) {
        await getContractApplication(contractId);
        setAbstractModalZ1({ isOpen: false });
        setAbstractModalZ2({ isOpen: false });
        setBaseModal({ isOpen: false });
      }
    };
  };

  const openApplicationModal = async (contractId: number) => {
    const contractApplication = await getContractUsageApplication({ contractId: contractId });
    const applicationSummary = await getApplicationSummury({
      quotationNo: "", // 데이터가 없음
      contractApplication: contractApplication!,
      contractId,
    });

    // 수정 form 오픈
    const openApplicationEditModal = () => {
      const formData: ProductApplication = {
        formData: {
          richText: contractApplication?.applicationBody?.formData?.richText || "",
          attachmentConfig: {
            isRequired: contractApplication?.applicationBody?.formData?.attachmentConfig?.isRequired || false,
          },
        },
      };

      setAbstractModalZ2({
        isOpen: true,
        children: (
          <UsageApplicationForm
            fileList={contractApplication?.applicationBody?.attachments || []}
            onClose={() => setAbstractModalZ2({ isOpen: false })}
            applicationSummary={applicationSummary}
            applicationFormData={formData}
            onSaveFormData={(data: ApplicationFormModel) => onUpdateCtApplication(data, contractId)}
          />
        ),
      });
    };

    setAbstractModalZ1({
      isOpen: true,
      children: (
        <UsageApplicationDetail //
          applicationValue={contractApplication?.applicationBody?.formData?.richText || ""}
          onClose={() => setAbstractModalZ1({ isOpen: false })}
          onEdit={openApplicationEditModal}
          applicationSummary={applicationSummary}
          fileList={contractApplication?.applicationBody?.attachments || []}
        />
      ),
    });
  };

  return (
    <div className="page-product-list">
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            <section>
              <div className="left-area__index">
                <span>지정검색</span>
              </div>
              <div className="left-area__contents">
                <div className="minmax160">
                  <BaseInput
                    type="text"
                    value={decodeURIComponent(params?.buildingName) || ""}
                    placeholder="건물 선택"
                    readonly={true}
                    onClick={(e: any) => {
                      e.preventDefault();
                      setIsBuildingOpen(true);
                    }}
                    onClearClick={() => {
                      let cl = _.cloneDeep(params);
                      cl.buildingName = "";
                      delete cl.buildingId;
                      navigateWithQueryParams({ ...params, ...cl });
                    }}
                  />
                  {isBuildingOpen && (
                    <SearchBuildingPopup
                      isOpen={isBuildingOpen}
                      partnerId={params.partnerId}
                      buildingName={""}
                      defaultChecked={params.buildingName ? [{ buildingId: String(params.buildingId), buildingName: params.buildingName }] : []}
                      onClick={settingBuilding}
                      onClose={() => setIsBuildingOpen(false)}
                    />
                  )}
                </div>
                <div className="minmax160">
                  <BaseInput
                    type="text"
                    value={decodeURIComponent(params?.orgName || "")}
                    placeholder="계약자 선택"
                    readonly={true}
                    onClick={(e: any) => {
                      e.preventDefault();
                      setIsOrgOpen(true);
                    }}
                    onClearClick={() => {
                      let cl = _.cloneDeep(params);
                      cl.orgName = "";
                      delete cl.mbOrganizationId;
                      navigateWithQueryParams({ ...params, ...cl });
                    }}
                  />
                  {isOrgOpen && (
                    <SearchOrgPopup
                      defaultChecked={params.orgName ? [String(params.mbOrganizationId)] : []}
                      isOpen={isOrgOpen}
                      onClick={settingOrg}
                      onClose={() => setIsOrgOpen(false)}
                    />
                  )}
                </div>
              </div>
            </section>
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>
              <div className="left-area__contents">
                {/* 조건검색 순서: 검색어 - 계약상태 - 승인유형 - 승인상태 - 상품유형 - 기간 옵션 */}
                <div>
                  <BaseInputWithSearch
                    type="text"
                    selectValue={params.searchType}
                    inputValue={decodeURIComponent(String(params?.searchValue) || "")}
                    stateOptions={searchTypeOption}
                    setStateValue={(searchType: string) => {
                      setParams({ ...params, searchType });
                    }}
                    onChange={(keyword: string) => setParams({ ...params, ...{ searchValue: keyword } })}
                    onKeyUp={() => navigateWithQueryParams({ ...params, ...{ page: 0 } })}
                    onSearchClick={() => navigateWithQueryParams({ ...params, ...{ page: 0 } })}
                    onClearClick={() => navigateWithQueryParams({ ...params, ...{ searchValue: "" } })}
                  />
                </div>
                <div className="minmax160">
                  <MultiSelectFilter
                    placeholder="계약상태"
                    options={searchStatusOption}
                    value={returnContractSteps || []}
                    onChange={(values) => {
                      let dump = _.cloneDeep(params);
                      dump.page = 0;
                      dump.contractStep = values.join(",");
                      navigateWithQueryParams(dump);
                    }}
                  />
                </div>
                <div className="minmax160">
                  <BaseSelect
                    value={params.approvalType}
                    stateOptions={approvalTypeOptions}
                    setStateValue={(value: string) => {
                      navigateWithQueryParams({ ...params, ...{ approvalType: value, page: 0 } });
                    }}
                    placeholder="승인유형"
                  />
                </div>
                <div className="minmax160">
                  <BaseSelect
                    value={params.approvalStatus}
                    stateOptions={CONTRACT_APPROVAL_STATUS_OPTIONS}
                    setStateValue={(value: string) => {
                      navigateWithQueryParams({ ...params, ...{ approvalStatus: value, page: 0 } });
                    }}
                    placeholder="승인상태"
                  />
                </div>
                {!isS2Contract && (
                  <div className="minmax160">
                    <BaseSelect
                      value={params.productTypes}
                      stateOptions={ProductType}
                      setStateValue={(value: string) => {
                        navigateWithQueryParams({ ...params, ...{ productTypes: value, page: 0 } });
                      }}
                      placeholder="상품 유형"
                    />
                  </div>
                )}
                <div className="minmax240">
                  {/* 기간 검색: 이용 시작일 ~ 이용 종료일 (기간 옵션 셀렉트 미사용) */}
                  <RangeDatepicker
                    dateRange={dateRange}
                    onChange={handleOnDateRangeChange}
                    placeholder="이용기간 선택"
                    onClearClick={() => {
                      navigateWithQueryParams({ ...params, searchStartTime: undefined, searchEndTime: undefined, page: 0 });
                    }}
                  />
                </div>
                <SearchResetButton />
              </div>
            </section>
          </div>
          <div className="right-area">
            {isAuthority("w") && <BaseButton title={`+ ${isS2Contract ? "공간예약 생성" : "신청/계약 생성"}`} onClick={() => navigate("form")} />}
          </div>
        </div>

        {/* 샘플 */}
        {data !== undefined && (
          <BaseTable
            data={data}
            isS2Contract={isS2Contract}
            columns={contractListColumns}
            pageIndex={Number(params?.page) || 0}
            currentSize={Number(queryParams.size) || 20}
            sizeOption={(sizeValue) => {
              navigateWithQueryParams({ ...params, size: sizeValue, page: 0 });
            }}
            totalPages={Number(pagination?.totalPages) || 0}
            totalElements={Number(pagination?.totalElements) || 0}
            goPage={(page: number) => {
              // setParams({ ...params, ...{ page } });
              navigateWithQueryParams({ ...params, ...{ page, size: queryParams.size } });
            }}
            disabledSortHeaders={[
              "mbOrganizationName",
              "memberMaxNums",
              "memberTotCount",
              "approvalType",
              "approvalStatus",
              "applicationId",
              "contractTerm",
            ]}
            orders={params?.sort?.orders}
            setOrders={(orders?: Array<Order>) => {
              if (orders) {
                // setParams({ ...params, ...{ sort: { orders } } });
                navigateWithQueryParams({ ...params, ...{ sort: { orders } } });
              }
            }}
            openApplicationModal={openApplicationModal}
            children={<ExcelDownloadButton onClick={handleClickExcelDownloadButton}>엑셀받기</ExcelDownloadButton>}
          />
        )}
      </div>
    </div>
  );
};

export default ContractList;
